import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import {
  Model,
  App,
  ArrayApp,
  AppRegistry,
  ModelRegistry,
  Input,
} from 'core/index';

import { analytics } from 'core/analytics';

import { FormStore } from 'libs/forms/app';

import { clientStore } from 'apps/client/app';
import { PermissionedModel } from 'apps/permissions/app';
import { modalStore } from 'apps/modals/app';
import { raiseUserError } from 'apps/utils';

import {
  InviteUsersToGroup,
  RemoveUserFromGroup,
  RemoveInvite,
  AcceptInvite,
  RejectInvite,
  CreateInviteLink,
  DeleteInviteLink,
  MoveUserToGroup,
  MoveInviteToGroup,
} from 'apps/group/gql';

import { conf } from 'apps/group/config.yaml';

const getInviteTargetRoute = (spaceType, spaceSlug, slug) => {
  if (!spaceType) return {};
  let parentSpaceType = null;
  if (spaceSlug) {
    parentSpaceType = spaceSlug[0] === '+' ? 'initiative' : 'profile';
  }
  switch (spaceType) {
    case 'SHARED_FILE': {
      if (!parentSpaceType) return {};
      return {
        name: `${parentSpaceType}.shared.file`,
        params: {
          [parentSpaceType]: spaceSlug,
          sharedFileId: slug,
        },
      };
    }
    case 'PROJECT': {
      if (!parentSpaceType) return {};
      return {
        name: `${parentSpaceType}.project`,
        params: {
          [parentSpaceType]: spaceSlug,
          slug,
        },
      };
    }
    case 'INITIATIVE':
      return {
        name: 'initiative',
        params: { initiative: `+${slug}` },
      };
    default:
      return {};
  }
};

export class InviteModel extends Model {
  static registryName = 'InviteModel';
  static ModelProps = {
    group: 'GroupModel',
    groupAvatar: 'FileModel',
    profile: 'ProfileModel',
  };

  @observable inviteDate;
  @observable email;
  @observable profile;
  @observable status;
  @observable moreInvitesRequested;
  @observable group = null;
  @observable groupTitle = '';
  @observable groupDescriptionSnippet = '';
  @observable groupName = '';
  @observable groupSpaceType = '';
  @observable groupSpaceSlug = '';
  @observable groupSlug = '';
  @observable groupSpacePrivate = false;
  @observable groupAvatar = null;

  @computed get username() {
    return (this.profile?.username || '').replace('@', '');
  }

  @computed get displayName() {
    return this.username ? `@${this.username}` : this.email;
  }

  @computed get imageFallbackChar() {
    const text = this.username || this.displayName;
    return text ? text[0] : 'a';
  }

  @computed get imageFallbackPath() {
    return `/assets/avatars/${this.imageFallbackChar}.jpeg`;
  }
}
ModelRegistry.add(InviteModel);

export class InviteApp extends App {
  static registryName = 'InviteApp';
  static AppProps = {
    group: 'GroupApp',
    groupAvatar: 'FileApp',
    profile: 'ProfileApp',
  };

  static Model = InviteModel;

  @computed get route() {
    return getInviteTargetRoute(
      this.groupSpaceType,
      this.groupSpaceSlug,
      this.groupSlug,
    );
  }

  @action
  Accept = (options = {}) => {
    const input = new Input({ inviteId: this.id });
    const update = new AcceptInvite(input);
    if (options.onSuccess) {
      update.setCallbacks(options);
    } else {
      update.setCallbacks({
        onSuccess: this.onAcceptSuccess.bind(this),
        onUserErr: this.onError,
        onSysErr: this.onError,
      });
    }
    update.mutate();
  };

  @action
  Reject = (options = {}) => {
    const input = new Input({ inviteId: this.id });
    const update = new RejectInvite(input);
    if (options.onSuccess) {
      update.setCallbacks(options);
    } else {
      update.setCallbacks({
        onSuccess: this.onRejectSuccess,
        onUserErr: this.onError,
        onSysErr: this.onError,
      });
    }
    update.mutate();
  };

  @action
  onError = () => {
    Alert.error('Whoops! There was an error handling that invitation.');
  };

  async onAcceptSuccess() {
    await clientStore.WhoAmI();
    switch (this.groupSpaceType) {
      case 'SHARED_FILE':
        Alert.success('Invitation to shared file accepted.');
        break;
      case 'INITIATIVE':
        Alert.success(
          'The invitation has been accepted. Welcome to the organisation!',
        );
        break;
      default:
        Alert.success('The invitation has been accepted.');
    }
    this.SetStatus('APPROVED');
  }

  onRejectSuccess = () => {
    Alert.success('The invitation has been rejected.');
    this.SetStatus('REJECTED');
  };

  @action
  SetStatus = status => {
    this.status = status;
  };
}
AppRegistry.add(InviteApp);

export class InvitesArrayApp extends ArrayApp {
  static registryName = 'InvitesArrayApp';
  static ElementApp = InviteApp;
}
AppRegistry.add(InvitesArrayApp);

export class GroupMemberModel extends PermissionedModel {
  static registryName = 'GroupMemberModel';
  static ModelProps = {
    profile: 'ProfileModel',
  };

  @observable id = '';
  @observable intrinsic;
  @observable profile = null;
}
ModelRegistry.add(GroupMemberModel);

export class GroupMemberApp extends App {
  static registryName = 'GroupMemberApp';
  static AppProps = {
    profile: 'ProfileApp',
  };

  static Model = GroupMemberModel;
}
AppRegistry.add(GroupMemberApp);

export class GroupMembersApp extends ArrayApp {
  static registryName = 'GroupMembersApp';
  static ElementApp = GroupMemberApp;
}
AppRegistry.add(GroupMembersApp);

export class GroupModel extends PermissionedModel {
  static registryName = 'GroupModel';
  static ModelProps = {
    members: ['GroupMemberModel'],
    pendingInvites: ['InviteModel'],
    space: 'SpaceModel',
  };

  @observable id = '';
  @observable space = '';
  @observable name = '';
  @observable members = [];
  @observable pendingInvites = [];
  @observable parent = null;
  @observable canAddMember = false;
  @observable content = null;

  @computed get parentName() {
    if (this.parent) {
      return (
        this.parent.name.charAt(0).toUpperCase() + this.parent.name.slice(1)
      );
    }
    return '';
  }

  @computed get computedModel() {
    if (!(this.upstream?.computedModel && this.content.__typename)) return null;
    const ModelClass = ModelRegistry.get(`${this.content.__typename}Model`);
    return ModelClass ? ModelClass.Load(this.upstream.computedModel) : null;
  }
}
ModelRegistry.add(GroupModel);

export class GroupApp extends App {
  static registryName = 'GroupApp';
  static AppProps = {
    members: 'GroupMembersApp',
    pendingInvites: 'InvitesArrayApp',
    space: 'SpaceApp',
  };

  static Model = GroupModel;

  @observable newMembers = null;
  @observable loading = {
    inviteMembers: false,
  };

  @computed get route() {
    if (!this.contentApp) return {};
    return this.contentApp.route;
  }

  init() {
    this.membersForm = new FormStore(this, conf, 'membersForm', '', false);
    const ProfilesApp = AppRegistry.get('ProfilesApp');
    this.newMembers = new ProfilesApp();
  }

  @computed get contentApp() {
    if (!(this.content && this.content.__typename)) return null;
    const AppClass = AppRegistry.get(`${this.content.__typename}App`);
    return AppClass ? new AppClass(this, this.content) : null;
  }

  @action
  InviteMembers = () => {
    if (this.membersForm.isValid) {
      this.membersInput = new Input({
        spaceId: this.space.id,
        groupName: this.name,
        invites: this.newMembers.map(({ username }) => username),
      });

      const update = new InviteUsersToGroup(this.membersInput);

      this.setIsLoading('inviteMembers', true);

      update.process(group => GroupModel.Load(group));
      update.setCallbacks({
        onSysErr: this.onInviteErr,
        onUserErr: this.onInviteErr,
        onSuccess: this.onInviteSuccess,
      });
      update.mutate();
    }
  };

  @action setIsLoading(key, value) {
    this.loading[key] = value;
  }

  @computed get isLoading() {
    return this.loading;
  }

  @action
  onInviteErr = error => {
    if (this.newMembers.length === 1) {
      raiseUserError(error, 'invite', 'User');
    } else {
      raiseUserError(error, 'invite', 'Users');
    }
    this.setIsLoading('inviteMembers', false);
  };

  @action
  onInviteSuccess = () => {
    if (this.membersInput.invites.length === 1) {
      Alert.success(
        `We've sent an invitation to ${this.membersInput.invites[0]}.`,
      );
    } else {
      const commaDelimitedUsers = this.membersInput.invites
        .slice(0, this.membersInput.invites.length - 1)
        .join(', ')
        .concat(
          ` and ${
            this.membersInput.invites[this.membersInput.invites.length - 1]
          }`,
        );
      Alert.success(`We've sent an invitation to ${commaDelimitedUsers}.`);
    }
    analytics.sendEvent('invite-project');
    analytics.sendEvent('invite-project-email');
    this.setIsLoading('inviteMembers', false);
    const ProfilesApp = AppRegistry.get('ProfilesApp');
    this.newMembers = new ProfilesApp();
  };

  @action
  RemoveInvite = inviteId => {
    const inviteInput = new Input({ inviteId });
    const update = new RemoveInvite(inviteInput);
    update.process(group => GroupModel.Load(group));
    this.setIsLoading(inviteId, true);
    update.setCallbacks({
      onSysErr: this.onRemoveErr(inviteId),
      onUserErr: this.onRemoveErr(inviteId),
      onSuccess: this.onRemoveSuccess(inviteId),
    });
    update.mutate();
  };

  @action
  RemoveMember = username => {
    const memberInput = new Input({
      spaceId: this.space.id,
      groupName: this.name,
      username,
    });
    // NOTE: this.manager is null (possibly related to the set locals bug),
    // hence putting the mutation onto the app for now.
    const update = new RemoveUserFromGroup(memberInput);
    this.setIsLoading(username, true);
    update.process(group => GroupModel.Load(group));
    update.setCallbacks({
      onSysErr: this.onRemoveErr(username),
      onUserErr: this.onRemoveErr(username),
      onSuccess: this.onRemoveSuccess(username),
    });
    update.mutate();
  };

  @action
  onRemoveErr = loadingId => error => {
    raiseUserError(error, 'remove', 'User');
    this.setIsLoading(loadingId, false);
  };

  @action
  onRemoveSuccess = loadingId => () => {
    modalStore.ClearComponent();
    Alert.success('User removed successfully.');
    this.setIsLoading(loadingId, false);
  };
}
AppRegistry.add(GroupApp);

export class GroupsApp extends ArrayApp {
  static registryName = 'GroupsApp';
  static ElementApp = GroupApp;
  @computed
  get sortedGroups() {
    // owner > (admin || moderator) > (member || collaborator) > limited-collaborator
    const sortValue = {
      owner: 0,
      admin: 1,
      moderator: 1,
      member: 2,
      collaborator: 2,
      'limited-collaborator': 3,
    };

    return this.apps.sort((groupA, groupB) => {
      const groupASortValue = sortValue[groupA.name];
      const groupBSortValue = sortValue[groupB.name];
      if (groupASortValue < groupBSortValue) return -1;
      if (groupASortValue > groupBSortValue) return 1;
      return groupA.name.localeCompare(groupB.name);
    });
  }

  @computed get groupsDistinctMembers() {
    const memberProfileIds = new Set();

    if (!Array.isArray(this.sortedGroups)) return [];

    return this.sortedGroups.map(group => {
      const modelData = group.upstream?.toJs(null, true);
      if (!modelData) {
        return group;
      }
      const filteredMembers = group.members.filter(
        member => !memberProfileIds.has(member.profile?.id),
      );
      filteredMembers.forEach(member =>
        memberProfileIds.add(member.profile?.id),
      );
      const groupApp = new GroupApp(null, modelData);
      groupApp.init();
      groupApp.members = filteredMembers;
      return groupApp;
    });
  }

  // flattened array of members + pending invites
  // TODO: alex separate 2 collections
  @computed get participants() {
    return [
      ...this.groupsDistinctMembers.map(group =>
        group.members.map(groupMember => ({
          displayName: groupMember.profile.username,
          profile: groupMember.profile,
          group,
          groupMember,
        })),
      ),
      ...this.map(group =>
        group.pendingInvites.map(invite => ({
          displayName: invite.displayName,
          profile: invite.profile,
          invite,
          group,
        })),
      ),
    ]
      .flat()
      .sort((participantA, participantB) => {
        if (participantA.group.name === 'admin') {
          return -1;
        }
        if (participantB.group.name === 'admin') {
          return 1;
        }
        if (participantA.groupMember && !participantB.groupMember) {
          return -1;
        }
        if (!participantA.groupMember && participantB.groupMember) {
          return 1;
        }
        return participantA.displayName.localeCompare(participantB.displayName);
      });
  }

  @action onMoveErr = error => {
    raiseUserError(error, 'move', 'User');
  };

  @action onMoveSuccess = () => {
    Alert.success('Permissions modified successfully!');
  };

  @action MoveInviteToGroup = (inviteId, groupId, options = {}) => {
    const inviteInput = new Input({
      inviteId,
      groupId,
    });
    const { onSuccess, onSysErr, onUserErr } = options;
    const update = new MoveInviteToGroup(inviteInput);
    update.setCallbacks({
      onSysErr: onSysErr || this.onMoveErr,
      onUserErr: onUserErr || this.onMoveErr,
      onSuccess: onSuccess || this.onMoveSuccess,
    });
    update.mutate();
  };

  @action MoveMemberToGroup = (userId, groupId, options = {}) => {
    const group = this.find(groupApp => groupApp.id === groupId);
    const memberInput = new Input({
      spaceId: group.space.id,
      userId,
      groupId,
    });
    const { onSuccess, onSysErr, onUserErr } = options;

    const update = new MoveUserToGroup(memberInput);
    update.setCallbacks({
      onSysErr: onSysErr || this.onMoveErr,
      onUserErr: onUserErr || this.onMoveErr,
      onSuccess: onSuccess || this.onMoveSuccess,
    });
    update.mutate();
  };

  @action MoveParticipant = (participant, targetGroupId) => {
    const sourceGroupId = participant.group.id;
    const sourceGroup = this.apps.find(group => group.id === sourceGroupId);
    const targetGroup = this.apps.find(group => group.id === targetGroupId);
    if (!(sourceGroup && targetGroup)) return;
    const onSuccess = result => {
      if (!result) return;
      const { fromGroup, toGroup } = result;
      GroupModel.Load(fromGroup);
      GroupModel.Load(toGroup);
      this.ResetLocals();
      this.onMoveSuccess();
    };
    const { invite, groupMember } = participant;
    if (invite) {
      sourceGroup.pendingInvites.remove(invite);
      targetGroup.pendingInvites.push(invite);
      this.MoveInviteToGroup(invite.id, targetGroupId, { onSuccess });
    } else {
      sourceGroup.members.remove(groupMember);
      targetGroup.members.push(groupMember);
      this.MoveMemberToGroup(groupMember.profile.user.id, targetGroupId, {
        onSuccess,
      });
    }
  };
}
AppRegistry.add(GroupsApp);

export class InviteLinkModel extends Model {
  static registryName = 'InviteLinkModel';
  static ModelProps = {
    content: 'ContentModel',
  };

  @observable url;
  @observable expiresAt;
  @observable content = null;
}

export class InviteLinkApp extends App {
  static registryName = 'InviteLinkApp';
  static Model = InviteLinkModel;
  static AppProps = {
    content: 'ContentApp',
  };

  @observable expiresInDays;
  init() {
    this.linkForm = new FormStore(this, conf, 'linkForm', '', false);
  }

  @computed get route() {
    const spaceType = this.content?.inSpace?.spaceType;
    const parentSpaceSlug = this.content?.inSpace?.parent?.path;
    const slug = this.content?.slug;
    return getInviteTargetRoute(spaceType, parentSpaceSlug, slug);
  }

  @action
  CreateLink = () => {
    if (!this.linkForm.isValid) {
      this.linkForm.alert();
      return;
    }
    this.linkForm.SetPosting(true);
    const input = new Input({
      spaceSlug: this.manager.variables.spaceSlug,
      slug: this.manager.variables.slug,
      expiresInDays: this.expiresInDays,
    });
    this.manager.mutations.push(
      CreateInviteLink.New(input, {
        onSysErr: this.OnCreateLinkError,
        onUserErr: this.OnCreateLinkError,
        onSuccess: this.OnCreateLinkSuccess,
      }),
    );
  };

  @action
  OnCreateLinkError = error => {
    raiseUserError(error, 'create', 'Link');
  };

  @action
  OnCreateLinkSuccess = () => {
    analytics.sendEvent('invite-project');
    analytics.sendEvent('invite-project-magiclink');
    this.linkForm.SetPosting(false);
    this.linkForm.clearDraft();
    this.manager.NewLinkQuery();
  };

  @action
  CopyLinkToClipboard = () => {
    // Create new element
    const el = document.createElement('textarea');
    // Set value (string to be copied)
    el.value = this.url;
    // Set non-editable to avoid focus and move outside of view
    el.setAttribute('readonly', '');
    el.style = { position: 'absolute', left: '-9999px' };
    document.body.appendChild(el);
    // Select text inside element
    el.select();
    // Copy text to clipboard
    // FIXME This will be deprecated https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
    document.execCommand('copy');
    // Remove temporary element
    document.body.removeChild(el);
    Alert.success('Link copied to clipboard.');
  };

  @action
  DeleteLink = () => {
    const input = new Input({
      inviteLinkId: this.id,
    });
    this.manager.mutations.push(
      DeleteInviteLink.New(input, {
        onSysErr: this.OnDeleteLinkError,
        onUserErr: this.OnDeleteLinkError,
        onSuccess: this.OnDeleteLinkSuccess,
      }),
    );
  };

  @action
  OnDeleteLinkError = error => {
    raiseUserError(error, 'delete', 'Link');
  };

  @action
  OnDeleteLinkSuccess = () => {
    this.manager.NewLinkQuery();
  };
}
